import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader, Disclaimer } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Music, FileText, Send, ShieldAlert, AlertTriangle, CheckCircle2, ScrollText, Gavel, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth, canWrite } from "@/lib/auth";

function Kpi({ label, value, hint, tone = "default", testId }) {
  const toneStyles = {
    default: { bg: "bg-card", accent: "hsl(347 72% 22%)" },
    warn: { bg: "bg-card", accent: "hsl(28 80% 38%)" },
    bad: { bg: "bg-card", accent: "hsl(0 70% 42%)" },
    good: { bg: "bg-card", accent: "hsl(150 50% 30%)" },
    gold: { bg: "bg-card", accent: "hsl(40 48% 40%)" },
  }[tone];
  return (
    <Card className={`${toneStyles.bg} relative overflow-hidden`} data-testid={testId}>
      <CardContent className="p-5">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="font-display text-3xl mt-1" style={{color: toneStyles.accent}}>{value ?? "—"}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: toneStyles.accent }} />
    </Card>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data)).catch(e => toast.error(errMsg(e)));
  }, []);

  const c = data?.counts || {};
  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Operating Dashboard"
        subtitle="GFMP · Kandake Prime · Internal Alpha"
        actions={
          writeAccess ? (
            <>
              <Button variant="outline" onClick={()=>navigate("/license-requests/new")} data-testid="dashboard-new-request-button"><FileText className="w-4 h-4 mr-2"/>New Request</Button>
              <Button onClick={()=>navigate("/tracks/new")} data-testid="dashboard-new-track-button" style={{background:"hsl(347 72% 22%)"}}><Plus className="w-4 h-4 mr-2"/>Add Track</Button>
            </>
          ) : (
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="dashboard-readonly-badge">Read-only access</span>
          )
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Kpi label="Total Tracks"            value={c.total_tracks}            testId="kpi-total-tracks" />
        <Kpi label="Pending Review"          value={c.pending_review}          tone="warn" testId="kpi-pending-review" />
        <Kpi label="Internally Ready"        value={c.internally_ready}        testId="kpi-internally-ready" />
        <Kpi label="Client-Ready"            value={c.client_ready}            tone="good" testId="kpi-client-ready" />
        <Kpi label="Corporate-Ready"         value={c.corporate_ready}         tone="gold" testId="kpi-corporate-ready" />
        <Kpi label="Attorney Review"         value={c.attorney_review_required} tone="bad" testId="kpi-attorney-review" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Missing Documents"       value={c.tracks_missing_documents} tone="warn" testId="kpi-missing-docs" />
        <Kpi label="Restricted"              value={c.restricted}              tone="bad" testId="kpi-restricted" />
        <Kpi label="Ineligible"              value={c.ineligible}              tone="bad" testId="kpi-ineligible" />
        <Kpi label="Open License Requests"   value={c.open_license_requests}   testId="kpi-open-requests" />
        <Kpi label="Draft Submissions"       value={c.draft_submissions}       testId="kpi-draft-submissions" />
        <Kpi label="Ready to Send"           value={c.ready_to_send_submissions} tone="good" testId="kpi-ready-to-send" />
      </div>
      {/* MP6 — license-request granular metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="New Requests"                   value={c.license_requests_new || 0}                       testId="kpi-requests-new" />
        <Kpi label="Requests In Review"             value={c.license_requests_in_review || 0}                 tone="warn" testId="kpi-requests-in-review" />
        <Kpi label="Approved for Submission"        value={c.license_requests_approved_for_submission || 0}   tone="good" testId="kpi-requests-approved" />
        <Kpi label="Requests Near Deadline"         value={c.license_requests_near_deadline || 0}             tone="bad" testId="kpi-requests-near-deadline" />
      </div>
      {/* MP7 — submission granular metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Internal Review"   value={c.internal_review_submissions || 0} tone="warn" testId="kpi-submissions-internal-review" />
        <Kpi label="Submissions Sent"  value={c.sent_submissions || 0}                       testId="kpi-submissions-sent" />
        <Kpi label="Shortlisted"       value={c.shortlisted_submissions || 0}   tone="good"  testId="kpi-submissions-shortlisted" />
        <Kpi label="Selected"          value={c.selected_submissions || 0}      tone="good"  testId="kpi-submissions-selected" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1" data-testid="priority-alerts-panel">
          <CardContent className="p-5">
            <div className="font-display text-xl flex items-center gap-2 mb-3"><ShieldAlert className="w-4 h-4" style={{color:"hsl(0 70% 42%)"}}/> Priority Alerts</div>
            <div className="space-y-2">
              <AlertRow
                icon={FolderOpen} tone="warn"
                label="Tracks missing required documents"
                count={c.tracks_missing_documents}
                onClick={()=>navigate("/compliance")}
                testId="alert-missing-docs"
              />
              <AlertRow
                icon={Gavel} tone="bad"
                label="Attorney Review Required"
                count={c.attorney_review_required}
                onClick={()=>navigate(`/tracks?status=${encodeURIComponent("Attorney Review Required")}`)}
                testId="alert-attorney-review"
              />
              <AlertRow
                icon={Send} tone="warn"
                label="Draft submissions awaiting internal review"
                count={c.draft_submissions}
                onClick={()=>navigate("/submissions?status=Draft")}
                testId="alert-draft-submissions"
              />
              <AlertRow
                icon={FileText} tone="default"
                label="Open license requests"
                count={c.open_license_requests}
                onClick={()=>navigate("/license-requests")}
                testId="alert-open-requests"
              />
            </div>
            <div className="text-[11px] text-muted-foreground mt-4">Compliance support only · Not legal advice · Human review required before external submission.</div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl flex items-center gap-2"><Music className="w-4 h-4"/> Recently Updated Tracks</div>
              <Button variant="ghost" size="sm" onClick={()=>navigate("/tracks")}>View all</Button>
            </div>
            <div className="divide-y">
              {(data?.recent_tracks || []).length === 0 && <div className="text-sm text-muted-foreground py-4">No tracks yet. Add your first track to begin.</div>}
              {(data?.recent_tracks || []).map(t => (
                <button key={t.id} onClick={()=>navigate(`/tracks/${t.id}`)} className="w-full text-left flex items-center justify-between py-3 hover:bg-secondary/40 rounded-md px-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.track_title || "(untitled)"}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.artist || "—"} · <span className="font-mono">{t.track_id}</span></div>
                  </div>
                  <StatusBadge status={t.clearance_status} />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="lg:col-span-1">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl flex items-center gap-2"><ScrollText className="w-4 h-4"/> Recent Audit Activity</div>
              <Button variant="ghost" size="sm" onClick={()=>navigate("/audit")}>Open log</Button>
            </div>
            <div className="divide-y">
              {(data?.recent_audit || []).length === 0 && <div className="text-sm text-muted-foreground py-4">No activity yet.</div>}
              {(data?.recent_audit || []).slice(0,8).map(a => (
                <div key={a.audit_id} className="py-2.5">
                  <div className="text-sm"><span className="font-medium">{a.action_type}</span> <span className="text-muted-foreground">on {a.record_type}</span></div>
                  <div className="text-xs text-muted-foreground font-mono">{a.timestamp?.slice(0,19).replace("T"," ")} · {a.user_email || "system"}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <Disclaimer />
    </div>
  );
}

function AlertRow({ icon: Icon, tone = "default", label, count, onClick, testId }) {
  const toneColor = tone === "bad" ? "hsl(0 70% 42%)" : tone === "warn" ? "hsl(28 80% 38%)" : "hsl(347 72% 22%)";
  const has = typeof count === "number" && count > 0;
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md hover:bg-secondary/60 border" data-testid={testId}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-md grid place-items-center shrink-0" style={{ background: has ? `${toneColor}1A` : "hsl(36 22% 92%)" }}>
          <Icon className="w-4 h-4" style={{ color: toneColor }} />
        </div>
        <div className="text-sm text-left truncate">{label}</div>
      </div>
      <div className="text-sm font-medium" style={{ color: has ? toneColor : "hsl(220 12% 40%)" }}>{count ?? "—"}</div>
    </button>
  );
}
